class BundleColorSelector {
  constructor(container) {
    this.container = container || document.querySelector('[data-bundle-color-selector]');
    if (!this.container) return;

    this.selectors = {
      trigger: '[data-bundle-color-trigger]',
      drawer: '[data-bundle-color-drawer]',
      closeButton: '[data-bundle-color-close]',
      option: '[data-bundle-color-option]',
      variantSelect: 'select[name="id"], [data-product-select]',
      quantity: '[data-bundle-quantity]',
      price: '[data-bundle-price]',
      variantTitle: '[data-bundle-variant-title]',
      dynamicVariantsContainer: '[data-dynamic-variants-enabled]'
    };

    this.drawer = this.container.querySelector(this.selectors.drawer);
    this.trigger = this.container.querySelector(this.selectors.trigger);
    this.closeButton = this.container.querySelector(this.selectors.closeButton);

    // Main product variant select & size buttons (shared for the whole product)
    this.variantSelect = document.querySelector(this.selectors.variantSelect) || null;
    this.dynamicVariantsContainer = document.querySelector(this.selectors.dynamicVariantsContainer) || null;

    // Bundle product ID for fetching variant data if needed
    this.bundleProductId = this.container.getAttribute('data-bundle-product-id');

    const indexAttr = this.container.getAttribute('data-bundle-index');
    this.isPrimary = (indexAttr == null || indexAttr == '0');

    this.classes = {
      active: 'is-active',
      selected: 'is-selected',
      soldOut: 'is-sold-out'
    };

    this.init();
  }

  init() {
    if (!this.container || !this.drawer || !this.trigger) return;

    this.bindEvents();
    this.setInitialSelected();

    this.container.setAttribute('data-initialized', 'true');
  }

  bindEvents() {
    // Open/Close drawer
    this.trigger.addEventListener('click', () => this.openDrawer());
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.closeDrawer());
    }

    // Option click
    const options = this.container.querySelectorAll(this.selectors.option);
    options.forEach(option => {
      option.addEventListener('click', () => this.handleOptionClick(option));
    });

    // Listen for variant:change event – all selectors should listen
    document.addEventListener('variant:change', (event) => {
      this.handleVariantChange(event);
    });
  }

  setInitialSelected() {
    const options = this.drawer.querySelectorAll('[data-bundle-color-option]');
    if (!options.length) return;

    // data-initial-selected is set in Liquid for the preselected variant
    let initialOption = this.drawer.querySelector('[data-bundle-color-option][data-initial-selected]');
    if (!initialOption) {
      initialOption = options[0];
    }

    options.forEach(option => {
      option.classList.toggle(this.classes.selected, option === initialOption);
    });

    const variantTitle = initialOption.dataset.variantTitle;
    const productTitle = initialOption.dataset.productTitle || this.container.dataset.bundleProductTitle || '';
    const quantity = parseInt(initialOption.dataset.quantity) || 1;
    const price = parseFloat(initialOption.dataset.price) || 0;
    const isSoldOut = initialOption.classList.contains(this.classes.soldOut);

    this.updateTrigger(variantTitle, productTitle, quantity, price, isSoldOut);
    // Run global ATC check after all selectors have initialised (deferred)
    setTimeout(() => checkAllBundleSoldOut(), 0);
  }

  getCurrentSize() {
    // Preferred: from the size radio buttons
    if (this.dynamicVariantsContainer) {
      const checked = this.dynamicVariantsContainer.querySelector(
        '.variant-input[data-index="option1"] input[type="radio"]:checked'
      );
      if (checked) return checked.value;
    }

    // Fallback: from the <select name="id">
    if (this.variantSelect) {
      const selectedId = this.variantSelect.value;
      const selectedOption = this.container.querySelector(`${this.selectors.option}.${this.classes.selected}`);
      if (selectedOption && selectedOption.dataset.variantId === selectedId) {
        // Size information would be in the variant title or option data
      }
    }

    return null;
  }

  openDrawer() {
    document.body.classList.add('bundle-color-drawer-open');
    this.drawer.classList.add(this.classes.active);
    if (window.theme && theme.a11y) theme.a11y.trapFocus({ container: this.drawer });
  }

  closeDrawer() {
    document.body.classList.remove('bundle-color-drawer-open');
    this.drawer.classList.remove(this.classes.active);
    if (window.theme && theme.a11y) theme.a11y.removeTrapFocus({ container: this.drawer });
  }

  handleOptionClick(option) {
    const variantId = option.dataset.variantId;
    const variantTitle = option.dataset.variantTitle;
    const productTitle = option.dataset.productTitle || this.container.dataset.bundleProductTitle || '';

    // Use default quantity for this selector position (fixed quantity per position)
    const defaultQuantity = parseInt(this.container.dataset.defaultQuantity) || 1;

    // Calculate price based on default quantity
    const price = this.calculatePrice(variantId, defaultQuantity);

    // Local selection state
    this.container.querySelectorAll(this.selectors.option).forEach(opt => {
      opt.classList.remove(this.classes.selected);
    });
    option.classList.add(this.classes.selected);

    // Update option data with default quantity
    option.dataset.quantity = defaultQuantity;
    option.dataset.price = price;
    const isSoldOut = option.classList.contains(this.classes.soldOut);

    this.updateTrigger(variantTitle, productTitle, defaultQuantity, price, isSoldOut);
    this.closeDrawer();
    checkAllBundleSoldOut();
  }

  updateDynamicVariant(variantTitle) {
    if (!this.dynamicVariantsContainer || !variantTitle) return;

    let variantInput = this.dynamicVariantsContainer.querySelector(
      `.variant-input[data-value="${variantTitle}"]`
    );

    if (!variantInput) {
      const allVariantInputs = this.dynamicVariantsContainer.querySelectorAll('.variant-input');
      allVariantInputs.forEach(inputEl => {
        const dataValue = inputEl.dataset.value;
        if (dataValue && (
          dataValue === variantTitle ||
          dataValue.includes(variantTitle) ||
          variantTitle.includes(dataValue)
        )) {
          variantInput = inputEl;
        }
      });
    }

    if (variantInput) {
      const input = variantInput.querySelector('input[type="radio"]');
      const label = variantInput.querySelector('label');

      if (input && !input.disabled && !input.classList.contains('disabled')) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.dispatchEvent(new Event('click', { bubbles: true }));
      } else if (label && !label.classList.contains('disabled')) {
        label.click();
      }
    }
  }

  handleVariantChange(event) {
    const variant = event.detail?.variant;
    const variantId = variant?.id || event.detail?.variantId;

    if (!variantId) {
      console.warn('Bundle selector: No variant ID found in event', event.detail);
      return;
    }

    try {
      let preselectedData = null;
      const metafieldJsonEl = document.querySelector('[data-variant-metafields-json]');

      if (metafieldJsonEl) {
        const metafieldData = JSON.parse(metafieldJsonEl.textContent);
        const variantData = metafieldData.variants.find(v => v.id.toString() === variantId.toString());
        if (variantData && variantData.preselected_bundle_color_with_quantity) {
          preselectedData = variantData.preselected_bundle_color_with_quantity;
        }
      } else {
        console.warn('Bundle selector: data-variant-metafields-json script tag not found. Variant change updates may not work correctly.');
        preselectedData = null;
      }

      const allSelectors = Array.from(document.querySelectorAll('[data-bundle-color-selector]'))
        .sort((a, b) => {
          const indexA = parseInt(a.dataset.bundleIndex) || 0;
          const indexB = parseInt(b.dataset.bundleIndex) || 0;
          return indexA - indexB;
        });

      if (preselectedData && preselectedData.length > 0) {
        const entries = [];
        preselectedData.forEach(entry => {
          if (entry && entry.includes('*')) {
            const parts = entry.split('*');
            entries.push({
              variantName: parts[0].trim(),
              quantity: parseInt(parts[1].trim()) || 1
            });
          }
        });

        allSelectors.forEach((selector, index) => {
          const selectorInstance = selector.bundleColorSelectorInstance;

          if (index < entries.length) {
            const parentBlock = selector.closest('.product-block');
            if (parentBlock && parentBlock.querySelector('[data-bundle-color-selector]') === selector) {
              parentBlock.style.display = '';
              parentBlock.style.visibility = '';
            }
            selector.style.display = '';
            selector.style.visibility = '';

            const entry = entries[index];
            selector.dataset.defaultQuantity = entry.quantity;

            if (selectorInstance) {
              selectorInstance.updateBundleSelectorForVariant(entry.variantName, entry.quantity);
            }
          } else {
            const parentBlock = selector.closest('.product-block');
            if (parentBlock && parentBlock.querySelector('[data-bundle-color-selector]') === selector) {
              parentBlock.style.display = 'none';
            }
            selector.style.display = 'none';
            selector.querySelectorAll('[data-bundle-color-option].is-selected').forEach(btn => btn.classList.remove('is-selected'));
          }
        });
      } else {
        allSelectors.forEach(selector => {
          const parentBlock = selector.closest('.product-block');
          if (parentBlock && parentBlock.querySelector('[data-bundle-color-selector]') === selector) {
            parentBlock.style.display = 'none';
          }
          selector.style.display = 'none';
          selector.querySelectorAll('[data-bundle-color-option].is-selected').forEach(btn => btn.classList.remove('is-selected'));
        });
      }

      // --- Point 2/3: Re-evaluate ATC after variant change hides/shows selectors ---
      checkAllBundleSoldOut();

    } catch (e) {
      console.warn('Failed to update bundle selector from variant change', e);
    }
  }

  updateBundleSelectorForVariant(variantName, quantity) {
    const options = this.container.querySelectorAll(this.selectors.option);
    if (!options.length) return;

    let matchedOption = null;

    // Update default quantity for this selector position
    this.container.dataset.defaultQuantity = quantity;

    options.forEach(option => {
      const variantTitle = option.dataset.variantTitle;
      const matches = variantTitle === variantName;

      if (matches) {
        matchedOption = option;
        const variantId = option.dataset.variantId;
        const price = this.calculatePrice(variantId, quantity);

        option.dataset.quantity = quantity;
        option.dataset.price = price;
        option.classList.add(this.classes.selected);

        this.container.dataset.preselectedVariantName = variantName;
        this.container.dataset.preselectedQuantity = quantity;
      } else {
        option.classList.remove(this.classes.selected);
        const defaultQty = parseInt(this.container.dataset.defaultQuantity) || quantity;
        const variantId = option.dataset.variantId;
        const price = this.calculatePrice(variantId, defaultQty);
        option.dataset.quantity = defaultQty;
        option.dataset.price = price;
      }
    });

    if (matchedOption) {
      const variantTitle = matchedOption.dataset.variantTitle;
      const qty = matchedOption.dataset.quantity || quantity;
      const price = matchedOption.dataset.price || 0;

      const productTitle = matchedOption.dataset.productTitle || this.container.dataset.bundleProductTitle || '';
      const isSoldOut = matchedOption.classList.contains(this.classes.soldOut);
      this.updateTrigger(variantTitle, productTitle, qty, price, isSoldOut);
    } else {
      const firstOption = options[0];
      if (firstOption) {
        const defaultQty = parseInt(this.container.dataset.defaultQuantity) || quantity;
        const variantId = firstOption.dataset.variantId;
        const price = this.calculatePrice(variantId, defaultQty);
        firstOption.classList.add(this.classes.selected);
        firstOption.dataset.quantity = defaultQty;
        firstOption.dataset.price = price;

        const fallbackProductTitle = firstOption.dataset.productTitle || this.container.dataset.bundleProductTitle || '';
        const isSoldOut = firstOption.classList.contains(this.classes.soldOut);
        this.updateTrigger(firstOption.dataset.variantTitle, fallbackProductTitle, defaultQty, price, isSoldOut);
      }
    }
  }

  updateFromDataAttributes() {
    const options = this.container.querySelectorAll(this.selectors.option);
    if (!options.length) return;

    let selectedOption = this.container.querySelector(
      `${this.selectors.option}.${this.classes.selected}`
    );
    if (!selectedOption) {
      selectedOption = options[0];
      selectedOption.classList.add(this.classes.selected);
    }

    const variantTitle = selectedOption.dataset.variantTitle;
    const quantity = parseInt(selectedOption.dataset.quantity) || 1;
    const price = parseFloat(selectedOption.dataset.price) || 0;

    // --- Point 2/3: Check sold-out on current option ---
    const isSoldOut = selectedOption.classList.contains(this.classes.soldOut);
    this.updateTrigger(variantTitle, quantity, price, isSoldOut);
  }

  getVariantMetafieldFromProduct(variantId) {
    console.warn('Bundle selector: Metafield JSON script tag not found. Please add data-variant-metafields-json script tag to product-template.liquid');
    return null;
  }

  calculatePrice(variantId, quantity) {
    const option = this.container.querySelector(`[data-bundle-color-option][data-variant-id="${variantId}"]`);
    if (option) {
      const defaultQuantity = parseInt(this.container.dataset.defaultQuantity) || 1;
      const priceForDefaultQty = parseFloat(option.dataset.price) || 0;
      const unitPrice = parseFloat(option.dataset.unitPrice);
      if (!isNaN(unitPrice)) {
        return unitPrice * quantity;
      }

      if (quantity === defaultQuantity) {
        return priceForDefaultQty;
      } else if (defaultQuantity > 0) {
        const pricePerUnit = priceForDefaultQty / defaultQuantity;
        return pricePerUnit * quantity;
      }
      return priceForDefaultQty;
    }

    return 0;
  }

  // isSoldOut param added — manages full-trigger overlay matching the drawer's ÉPUISÉ treatment
  updateTrigger(title, productTitle, quantity, price, isSoldOut) {
    if (!this.trigger) return;
    const titleEl = this.trigger.querySelector(this.selectors.variantTitle);
    const quantityEl = this.trigger.querySelector(this.selectors.quantity);
    const priceEl = this.trigger.querySelector(this.selectors.price);
    const imageContainer = this.trigger.querySelector('.bundle-color-trigger__image-container');

    // Update text content
    if (titleEl) titleEl.textContent = productTitle ? `${productTitle}: ${title}` : title;
    if (quantityEl) quantityEl.textContent = `${quantity} PCS.`;

    // Format price
    if (priceEl) {
      if (window.theme && theme.Currency) {
        priceEl.textContent = theme.Currency.formatMoney(price);
      } else {
        priceEl.textContent = typeof price === 'number' ? `$${(price / 100).toFixed(2)}` : price;
      }
    }

    // Sold-out overlay — covers full trigger, matching drawer ÉPUISÉ style.
    // Labels are read from data attributes set in Liquid via translation strings,
    // so they follow the store language automatically.
    if (isSoldOut) {
      this.trigger.classList.add(this.classes.soldOut);
      this.trigger.setAttribute('data-sold-out', 'true');
      // Ensure trigger is a positioning context for the absolute overlay
      this.trigger.style.position = 'relative';

      // Inject overlay only once — reuse if already present
      let overlay = this.trigger.querySelector('.bundle-trigger__soldout-overlay');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'bundle-trigger__soldout-overlay';

        const soldOutLabel = this.trigger.getAttribute('data-soldout-label') || 'Sold out';
        const changeLabel  = this.trigger.getAttribute('data-change-label')  || 'Change';

        const soldOutSpan = document.createElement('span');
        soldOutSpan.className = 'bundle-trigger__soldout-text';
        soldOutSpan.textContent = soldOutLabel;

        const changeSpan = document.createElement('span');
        changeSpan.className = 'bundle-trigger__change-text';
        changeSpan.textContent = changeLabel;

        overlay.appendChild(soldOutSpan);
        overlay.appendChild(changeSpan);
        // Overlay sits inside the trigger — clicks bubble up to trigger's openDrawer listener
        this.trigger.appendChild(overlay);
      }
    } else {
      this.trigger.classList.remove(this.classes.soldOut);
      this.trigger.removeAttribute('data-sold-out');
      this.trigger.style.position = '';

      const overlay = this.trigger.querySelector('.bundle-trigger__soldout-overlay');
      if (overlay) overlay.remove();
    }

    // Update trigger image to match selected option
    if (imageContainer) {
      try {
        const options = Array.from(this.container.querySelectorAll(this.selectors.option));
        let matchedOption = options.find(opt => opt.dataset.variantTitle === title);
        if (!matchedOption) matchedOption = this.container.querySelector(`${this.selectors.option}.is-selected`) || options[0];

        if (matchedOption) {
          const sourceImageEl = matchedOption.querySelector('.bundle-color-option__image');
          imageContainer.innerHTML = '';

          if (sourceImageEl) {
            const img = sourceImageEl.querySelector('img');
            if (img) {
              const newImg = img.cloneNode(true);
              newImg.classList.add('bundle-color-trigger__image');
              if (!newImg.hasAttribute('width')) newImg.setAttribute('width', '40');
              if (!newImg.hasAttribute('height')) newImg.setAttribute('height', '40');
              imageContainer.appendChild(newImg);
            } else {
              const newDiv = document.createElement('div');
              newDiv.className = 'bundle-color-trigger__image';
              const style = sourceImageEl.getAttribute('style') || '';
              if (style) newDiv.setAttribute('style', style);
              imageContainer.appendChild(newDiv);
            }
          } else {
            const placeholder = document.createElement('div');
            placeholder.className = 'bundle-color-trigger__image-placeholder';
            imageContainer.appendChild(placeholder);
          }
        }
      } catch (e) {
        // Fail silently
      }
    }
  }
}

// --- Point 2/3: Global function — checks all visible bundle selectors for sold-out state ---
// Updates the ATC button text and disabled state accordingly.
function checkAllBundleSoldOut() {
  // Find the add-to-cart button — it has data-bundle-submit
  const addToCartBtn = document.querySelector('[data-bundle-submit]');
  if (!addToCartBtn) return;

  // Only consider selectors that are currently visible (same filter used in addBundleItems)
  const visibleSelectors = Array.from(document.querySelectorAll('[data-bundle-color-selector]')).filter(function(selector) {
    if (selector.offsetParent === null) return false;
    var cs = window.getComputedStyle(selector);
    return cs.display !== 'none' && cs.visibility !== 'hidden' && cs.opacity !== '0';
  });

  const hasSoldOut = visibleSelectors.some(function(selector) {
    var trigger = selector.querySelector('[data-bundle-color-trigger]');
    return trigger && trigger.hasAttribute('data-sold-out');
  });

  // Resolve the original "add to cart" label to restore to.
  // data-original-text pre-seeded in Liquid takes priority so we never
  // accidentally capture the disabled label (rendered by Liquid on page load)
  // as the text to restore to.
  var resolvedOriginal = addToCartBtn.getAttribute('data-original-text') || addToCartBtn.dataset.originalTextJs || '';
  if (!resolvedOriginal) {
    // First JS run and no Liquid pre-seed — only capture current text when it
    // is not already the sold-out label
    var selectColorLabelCheck = addToCartBtn.getAttribute('data-select-color-label') || 'Select available color';
    var currentText = addToCartBtn.textContent.trim();
    if (currentText !== selectColorLabelCheck) {
      addToCartBtn.dataset.originalTextJs = currentText;
      resolvedOriginal = currentText;
    }
  }

  if (hasSoldOut) {
    // Label read from Liquid-set data attribute, fallback to English
    var selectColorLabel = addToCartBtn.getAttribute('data-select-color-label') || 'Select available color';
    addToCartBtn.disabled = true;
    addToCartBtn.textContent = selectColorLabel;
  } else {
    addToCartBtn.disabled = false;
    if (resolvedOriginal) {
      addToCartBtn.textContent = resolvedOriginal;
    }
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  initBundleColorSelectors();
  syncBundleSelectorsToCurrentVariant();
});

document.addEventListener('section:load', (event) => {
  if (event.target.querySelector('[data-bundle-color-selector]')) {
    initBundleColorSelectors();
    syncBundleSelectorsToCurrentVariant();
  }
});

function initBundleColorSelectors() {
  document.querySelectorAll('[data-bundle-color-selector]').forEach(container => {
    if (!container.hasAttribute('data-initialized')) {
      const instance = new BundleColorSelector(container);
      // Store instance reference for external access
      if (instance && instance.container) {
        instance.container.bundleColorSelectorInstance = instance;
      }
      container.setAttribute('data-initialized', 'true');
    }
  });
}

function syncBundleSelectorsToCurrentVariant() {
  try {
    const currentVariantEl = document.querySelector('[data-current-variant-json]');
    let currentVariant = null;
    if (currentVariantEl) {
      currentVariant = JSON.parse(currentVariantEl.textContent);
    }
    const masterSelect = document.querySelector('select[name="id"],[data-product-select]');
    const variantId = currentVariant?.id || (masterSelect && masterSelect.value);
    if (!variantId) return;

    document.querySelectorAll('[data-bundle-color-selector]').forEach(container => {
      const inst = container.bundleColorSelectorInstance;
      if (inst && typeof inst.handleVariantChange === 'function') {
        inst.handleVariantChange({ detail: { variant: { id: variantId } } });
      }
    });

    // --- Point 2/3: Run ATC check after initial sync ---
    checkAllBundleSoldOut();
  } catch (e) {
    console.warn('Bundle selector: failed to sync on load', e);
  }
}